<template>
  <v-text-field
    :class="compClass +
      ((getForAccessibility) ? ' for-accessibility-app ' : '')"
    outlined
    dense
    :maxlength="maxlength"
    background-color="white"
    persistent-placeholder
    :label="translateLabel"
    :placeholder="placeholder"
    :type="type"
    :disabled="disabled"
    :readonly="readonly"
    v-model.trim="elValue"
    v-mask="vMask"
    :hide-details="hideDetails"
    @blur="onBlur"
    @focus="onFocus"
    @change="onChange"
    @keypress="onKeyPress"
    v-validate="validateRules"
    :data-vv-name="validateName"
    :error-messages="errorMessages"
    :data-vv-validate-on="validateOn"
    :autofocus="autofocus"
  >
    <template v-if="tooltip" slot="label">
      <div style="display: flex">
        <span class="label-text">{{ `${translateLabel}` }}</span>
        <div style="color: red" v-if="required">*</div>
        <span v-if="emptyLine" style="white-space: pre; color: white">{{ `___` }}</span>
        <el-tooltip slot="append-outer" @addEmptyLine="addEmptyLine" :tooltip="tooltip" input />
      </div>
    </template>
    <!-- <template v-if="phoneNumber" slot="prepend-inner">
      <span style="line-height: 1.6rem; font-size: 12px">+370</span>
    </template> -->
    <template slot="append">
      <slot name="append"></slot>
    </template>
  </v-text-field>
</template>

<script>
import errorNotifications from '@/mixins/errorNotifications'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  name: 'ElTextField',
  mixins: [errorNotifications],
  props: {
    classP: String,
    label: String,
    placeholder: { type: String, default: ' ' },
    type: String,
    tooltip: String,
    counter: [Number, String],
    required: Boolean,
    need: Boolean,
    hideDetails: [Boolean, String],
    date: Boolean,
    time: Boolean,
    email: Boolean,
    phoneNumber: Boolean,
    phoneNumber2: Boolean,
    value: [String, Number],
    disabled: Boolean,
    readonly: Boolean,
    validateKey: String,
    pastDates: Boolean,
    pastDate: String,
    futureTermDates: String,
    futureDates: Boolean,
    today: String,
    autofocus: { type: Boolean, default: false },
    idk: Boolean,
    personalCode: Boolean,
    maxlength: [Number, String],
    max: [Number, String],
    min: [Number, String],
    length: [Number, String],
    afterDate: String,
    cMask: [Number, String],
    isPercentage: [Boolean],
    timeMask: Boolean,
    prependField: Boolean,
    digits:[Number, String],
    integer: Boolean,
    commaSeparatedDigits: Boolean,
    validateOnInput: Boolean,
  },
  data() {
    return {
      emptyLine: false,
    }
  },
  inject: {
    $validator: '$validator',
  },
  computed: {
    ...mapGetters(['getForAccessibility']),
    elValue: {
      get() {
        return this.value
      },
      set(elValue) {
        let value = elValue
        if (this.integer) {
          value = String(value ?? '').replace(/\D/g, '')
        } else if (this.commaSeparatedDigits) {
          value = String(value ?? '').replace(/\s/g, '').replace(/[^\d,]/g, '')
        }
        this.$emit('input', value)
      },
    },
    translateLabel() {
      return this.$t(this.label)
    },

    validateName() {
      return this.validateKey || this.label
    },
    validateMessages() {
      return {
        'The IS identifikatorius field must be at least': `Privalomi bent ${this.min} simboliai`,
        'field may not be greater than': `Viršytas simbolių skaičius ${this.maxLength}`,
        // 'length must be 11': this.notificationText('000023'),
        'length must be 11': this.personalCode ? this.notificationText('000023') : this.$t('Privaloma 11 simbolių'),
        'length must be 9': this.$t('Privalomi 9 simboliai'),
        'length must be 10': this.$t('Privaloma 10 simbolių'),
        'length must be 2': this.$t('Privalomi 2 simboliai'),
        'length must be 3': this.$t('Privalomi 3 simboliai'),
        'The phone-number-undefined field must be numeric and contains exactly 8 digits': 'Telefono numerio ilgis privalo būti 8 simbolių',
        'field must be at least 14': this.$t(
          `Identifikavimo kodas (IDK) turi būti 14 skaičių`
        ),
        'field is required':
          this.notificationText('000006') || this.$t('Laukas privalomas'),
        'must be a valid email':
          this.notificationText('000012') ||
          this.$t('Netinkamas el. pašto formatas'),
        'must be in the format yyyy-MM-dd':
          this.notificationText('000011') ||
          this.$t('Netinkamas datos formatas'),
        'must be between 0001-01-01': this.$t(
          'Data negali būti didesnė už šios dienos datą'
        ),
        'must be between 0001-01-02': this.$t(
          'Data negali būti vėlesnė nei šiandiena'
        ),
        'and 9999-01-01': this.$t(
          'Data negali būti ankstesnė už šios dienos datą'
        ),
        'and 9999-01-02': this.$t(
          'Data negali būti ankstesnė už galiojimo nuo datą'
        ),
        'and 9999-01-03':
          this.notificationText('000013') ||
          this.$t('Data negali būti ankstesnė už galiojimo nuo datą'),
        'must be an integer': this.$t('Laukas turi būti sveikasis skaičius'),
        'must be in the format HH:mm': this.$t('Netinkamas laiko formatas'),
        'The Termino data must be between': this.$t(
          'Data negali būti ankstesnė už šios dienos datą arba didesnė už įgaliojimo termino datą'
        ),
      }
    },
    validateRules() {
      let rule = ''
      if (!this.readonly) {
        rule = this.required ? 'required' : ''
        rule += this.email ? (rule ? '|' : '') + 'email' : ''
        rule += this.date ? (rule ? '|' : '') + 'date_format:yyyy-MM-dd' : ''
        if(this.date && this.futureDates && this.afterDate){
          const afterDate = moment(this.afterDate);
          const today = moment(this.today);
          if (afterDate.isAfter(today)) {
            rule += (rule ? '|' : '') + 'date_between:' + this.afterDate + ',9999-01-02,1'
          } else {
            rule += (rule ? '|' : '') + 'date_between:' + this.today + ',9999-01-01,1'
          }
        }
        rule +=
          this.date && this.futureDates && !this.afterDate
            ? (rule ? '|' : '') + 'date_between:' + this.today + ',9999-01-01,1'
            : ''
        rule +=
          this.date && this.pastDates
            ? (rule ? '|' : '') + 'date_between:0001-01-02,' + this.today + ',1'
            : ''
        rule +=
          this.date && this.futureTermDates
            ? (rule ? '|' : '') + 'date_between:' + this.futureTermDates + ',9999-01-03,1'
            : ''
        rule +=
          this.date && this.pastDate
            ? (rule ? '|' : '') +
              'date_between:' +
              this.today +
              ',' +
              this.pastDate +
              ',1'
            : ''
        rule +=
          this.date && this.afterDate && !this.futureDates
            ? (rule ? '|' : '') +
              'date_between:' +
              this.afterDate +
              ',9999-01-02,1'
            : ''
        rule += this.time ? (rule ? '|' : '') + 'date_format:HH:mm' : ''
        rule += this.counter ? (rule ? '|' : '') + `max:${this.maxLength}` : ''
        rule += this.idk ? (rule ? '|' : '') + `max:14` : ''
        rule += this.personalCode ? (rule ? '|' : '') + `length:11` : ''
        rule += this.length ? (rule ? '|' : '') + `length:${this.length}` : ''
        rule += this.max ? (rule ? '|' : '') + `max:${this.max}` : ''
        rule += this.min ? (rule ? '|' : '') + `min:${this.min}` : ''
        rule += this.digits ? (rule ? '|' : '') + `digits:${this.digits}` : ''
        rule += this.integer ? (rule ? '|' : '') + 'integer' : ''
      }
      return rule
    },
    validateOn() {
      if(this.validateOnInput){
        return 'input'
      }
      return (this.required &&
        !this.email &&
        !this.idk &&
        !this.phoneNumber &&
        !this.phoneNumber2 &&
        !this.personalCode) ||
        this.counter
        ? 'input'
        : 'change'
    },
    errorMessages() {
      const messages = this.errors.collect(this.validateName)
      if (messages.length > 0) {
        const key = Object.keys(this.validateMessages).find((item) =>
          messages[0].includes(item)
        )
        if(this.prependField) {return ''}
        return this.validateMessages[key]
      } else {
        return ''
      }
    },
    maxLength() {
      return this.counter
    },
    requiredClass() {
      return (this.required || this.need) && !this.readonly
        ? 'required-input'
        : ''
    },
    compClass() {
      return `${this.classP} ${this.requiredClass} ${this.prependField ? 'prepend-field' : ''} labelPatch`
    },
    vMask() {
      let mask = ''
      if (this.personalCode) {
        mask = '###########'
      }
      if (this.phoneNumber) {
        mask = '###############'
      }
      if (this.phoneNumber2) {
        mask = '########'
      }
      if (this.prependField) {
        mask = ['+' ,/[0-9_.+]/,/[0-9]/, /[0-9_.-]/, /[0-9_.-]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/]
      }
      if (this.idk) {
        mask = '##############'
      }
      if (this.timeMask && this.elValue) {
        const hours = [
          /[0-2]/,
          this.elValue.charAt(0) === '2' ? /[0-3]/ : /[0-9]/,
        ]
        const minutes = [/[0-5]/, /[0-9]/]
        mask = [...hours, ':', ...minutes]
      }
      if (this.cMask) {
        mask = '#'.repeat(this.cMask)
      }
      if (this.isPercentage) {
        if (this.elValue === '10') {
            mask = [/[0-1]/, /\d/, /\d/]
        } else if (this.elValue === '100') {
            mask = [/[0-1]/, /\d/, /\d/]
        } else if (this.elValue && this.elValue.length === 2 && parseInt(this.elValue) <= 99) {
            mask = [/\d/, /\d/]
        } else {
            mask = [/\d/, /\d/]
        }
      }
      return mask
    },
  },
  methods: {
    addEmptyLine(value){
      this.emptyLine = value
    },
    onBlur(e) {
      this.$emit('blur', e.target.value)
    },
    onFocus(e) {
      this.$emit('focus', e.target.value)
    },
    onChange(e) {
      this.$emit('change')
    },
    onKeyPress(event) {
      if (this.cMask || this.integer) {
        const key = event.key;
        if (/\D/i.test(key)) {
          event.preventDefault();
          return;
        }
      }
      if (this.commaSeparatedDigits) {
        const key = event.key;
        if (!/^[\d,]$/.test(key)) {
          event.preventDefault();
        }
      }
      // if(this.email){
      //   const allowedChars = /^[a-zA-Z0-9@._-]*$/;
      //   const key = String.fromCharCode(event.keyCode || event.which);
      //   if (!allowedChars.test(key)) {
      //     event.preventDefault();
      //   }
      // }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../assets/styles/elements.scss';
</style>
